import React from 'react';

import { Box, Stack, Typography } from '@mui/material';

import RouteLink from '@ivy/components/atoms/RouteLink';
import { ArrowRightIcon } from '@ivy/components/icons';
import SectionHeader, {
	type SectionHeaderProps,
} from '@ivy/components/molecules/SectionHeader';
import { PROFESSION2SLUG, Profession } from '@ivy/constants/clinician';
import { useCurrentAccount } from '@ivy/gql/hooks';
import { gql, type FragmentType, getFragmentData } from '@ivy/gql/types';
import { formatInteger } from '@ivy/lib/formatting/number';
import { buildInternalLink } from '@ivy/lib/util/route';

const OrgFacilities_OrgFDoc = gql(/* GraphQL */ `
	fragment OrgFacilities_Org on organization {
		id
		slug
		contracts(
			where: { active: { _eq: true } }
			order_by: [{ facility: { name: asc } }]
			limit: 5
		) {
			id
			facility {
				id
				name
				slug
			}
		}
		contractsAgg: contracts_aggregate(where: { active: { _eq: true } }) {
			aggregate {
				count
			}
		}
	}
`);

export interface OrgFacilitiesProps extends Omit<SectionHeaderProps, 'title'> {
	org: FragmentType<typeof OrgFacilities_OrgFDoc>;
}

const OrgFacilities = ({ org: rawOrg, ...props }: OrgFacilitiesProps) => {
	const org = getFragmentData(OrgFacilities_OrgFDoc, rawOrg);
	const currAcc = useCurrentAccount();

	if (!org.contractsAgg.aggregate?.count) {
		return null;
	}

	return (
		<SectionHeader title='Facilities' {...props}>
			<Stack spacing={3} mb={8}>
				{org.contracts.map((contract) => (
					<Typography
						variant='body1'
						fontWeight='bold'
						noWrap
						key={contract.id}
					>
						<RouteLink
							to={{
								pathname: buildInternalLink(RouteLink.routes.FACILITY_SHOW, {
									facilityId: [contract.facility.id, contract.facility.slug],
								}),
								whitelabel: 'default',
							}}
							underline='hover'
						>
							{contract.facility.name}
						</RouteLink>
					</Typography>
				))}
			</Stack>
			<Typography variant='body1' fontWeight='bold'>
				<RouteLink
					underline='hover'
					to={{
						pathname: buildInternalLink(RouteLink.routes.ORG_FACILITY_LIST, {
							orgId: [org.id, org.slug],
							slug: 'search',
							profession:
								PROFESSION2SLUG[
									currAcc?.clinician?.profession || Profession.PHYSICIAN
								],
						}),
						whitelabel: 'default',
					}}
				>
					<Box component='span' sx={{ verticalAlign: 'middle' }}>
						See all{' '}
						{org.contractsAgg.aggregate?.count > 1
							? `${formatInteger(org.contractsAgg.aggregate?.count)} locations`
							: 'locations'}
					</Box>
					<ArrowRightIcon
						sx={{
							verticalAlign: 'middle',
							ml: 1,
							fontSize: 'inherit',
						}}
					/>
				</RouteLink>
			</Typography>
		</SectionHeader>
	);
};

export default OrgFacilities;
